import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";

import { Router } from "@angular/router";
import { Subject } from "rxjs";

import { IPurchase } from "./../../../../shared/models/purchase";
import {
  Renewal,
  IRenewal,
} from "./../../../../shared/models/products/renewal";
import {
  RenewalPayment,
  RenewalDetail,
} from "./../../../../shared/models/payments/renewal-payment";
import { PurchaseService } from "./../../../../shared/services/purchase.service";
import { PurchaseStateService } from "./../../../../shared/services/purchase-state.service";
import { CommonApiService } from "./../../../../../shared/services/api/common-api.service";
import { RegexPatterns } from "./../../../../../shared/regex/regex-patterns";
import { AppSettingsService } from "./../../../../../shared/services/app-settings.service";
import { NotificationsService } from "./../../../../../shared/services/notifications.service";
import { ViewStateService } from "./../../../../../shared/services/view-state.service";
import { PayNowUser } from "app/purchase/shared/models/payments/pay-now-user";
import { debounceTime, takeUntil } from "rxjs/operators";
import { environment } from "environments/environment";

enum CardTypes {
  Amex,
  Discover,
  MasterCard,
  Visa,
}

declare var Accept: any;

@Component({
  selector: "app-paynow-payment",
  templateUrl: "./paynow-payment.component.html",
})
export class PayNowPaymentComponent implements OnInit, OnDestroy {
  purchase: IPurchase;
  user: PayNowUser;
  checkoutForm: UntypedFormGroup;
  serverMessage: string;
  cardTypes = CardTypes;
  ccType: CardTypes;
  countries: any;
  source: string = "";
  private destroy$ = new Subject<void>();

  constructor(
    private purchaseState: PurchaseStateService,
    private purchaseService: PurchaseService,
    private commonApiService: CommonApiService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private appSettings: AppSettingsService,
    private notificationsService: NotificationsService,
    private viewState: ViewStateService
  ) {}

  ngOnInit() {
    this.purchase = this.purchaseState.currentState;
    this.viewState.startLoading();

    let renewals = <Renewal>(
      this.purchase.products.find((p) => p.constructor === Renewal)
    );

    this.buildForm();
    this.getCountries();
    this.user = this.purchaseState.getPersonForPayNow();

    this.purchaseState.setPractitionerName(
      this.user.firstName.concat(" ").concat(this.user.lastName)
    );
    this.formValues.address = this.user.line1;
    this.formValues.nameOnCard = this.user.firstName
      .concat(" ")
      .concat(this.user.lastName);
    this.formValues.city = this.user.city;
    this.formValues.state = this.user.stateProvince;
    this.formValues.zip = this.user.zip;
    this.formValues.countryId = this.user.countryId.toString();

    this.checkoutForm.patchValue(this.formValues);

    this.purchaseService
        .loadAcceptJs()
        .then(() => {
          console.log("Accept.js is ready");
        })
        .catch((err) => {
          console.error(err);
        });

    this.viewState.stopLoading();

    this.checkoutForm.controls["cardNumber"].valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((v: string) => {
        if (v.match("^4[0-9]{12}(?:[0-9]{3})?$")) this.ccType = CardTypes.Visa;
        else if (
          v.match(
            "^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$"
          )
        )
          this.ccType = CardTypes.MasterCard;
        else if (v.match("^3[47][0-9]{13}$")) this.ccType = CardTypes.Amex;
        else if (v.match("^6(?:011|5[0-9]{2})[0-9]{12}$"))
          this.ccType = CardTypes.Discover;
        else this.ccType = null;
      });
  }
  private getCountries() {
    this.commonApiService.getCountries().subscribe(
      (res) => {
        this.countries = res;
      },
      (error) => {
        console.log(error);
      }
    );
  }

  private submitPaymentInitial() {
          const authData = {
            clientKey: environment.clientKey,
            apiLoginID: environment.apiLogin,
          };
      
          const cardData = {
            cardNumber: this.formValues.cardNumber,
            month: this.formValues.expirationDate.substring(0, 2),
            year: 20 + this.formValues.expirationDate.substring(2),
            cardCode: this.formValues.ccv,
          };
      
          const secureData = {
            authData,
            cardData,
          };
          //console.log(authData);
          //console.log(cardData);
      
          Accept.dispatchData(secureData, this.responseHandler.bind(this));
        }
      
        responseHandler(response: any) {
          if (response.messages.resultCode === "Error") {
            console.error(
              "Error creating payment nonce/token:",
              response.messages.message
            );
            console.log(response.messages);
            let err = response.messages.message[0].text;
            if (!err || err=="") 
              err = "Please review credit card information and resubmit";
            this.notificationsService.error(
              "Payment Failed",
              err
            );        
            this.viewState.stopLoading();
            return;
          }
      
          const opaqueData = response.opaqueData;
          
          this.submitPayment(opaqueData);
        }

  onSubmit(updatedForm) {
    this.formValues = updatedForm;
    this.validateForm();
    if (this.checkoutForm.valid) {
      this.viewState.startLoading();

      this.submitPaymentInitial();
    }
  }

  submitPayment(opaqueData) {
      let renewalProduct = [];
      this.purchaseState.currentState.products.forEach((element) => {
        renewalProduct.push(<Renewal>element);
      });
      let paymentParams = this.mapPurchaseToPayment(renewalProduct, opaqueData);
      this.purchaseState.setPaymentMethod(paymentParams.paymentType);
      this.purchaseState.currentState.paymentDate = new Date();
      this.purchaseService
        .sendRenewalPaymentPayNow(paymentParams, this.source)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (res) => {
            this.viewState.stopLoading();
            this.purchaseState.setCompletedPurchase(
              this.purchaseState.currentState
            );
            this.purchaseState.reset();
            this.router.navigate(["practitioner/paynow/checkout/confirmation"]);
          },
          (e) => {
            this.handleServerErrorResponse(e);
          }
        );
  }

  onCancel(event: Event) {
    event.preventDefault();

    this.purchaseState.reset();
    this.router.navigate([""]);
  }

  private handleServerErrorResponse(response) {
    if (response.status === 500) {
      this.notificationsService.error(
        "Unexpected Error",
        "An unexpected error occurred."
      );
      if (!this.appSettings.IsProductionMode) {
        console.log(response);
      }
    } else {
      this.mapServerErrorsToForm(response.error);
    }

    this.viewState.stopLoading();
  }

  private mapServerErrorsToForm(errors) {
    let unknownErrors = [];
    let firstErrorFieldName;

    for (let error of errors) {
      this.formErrors[error.fieldName] = "";

      const control = this.checkoutForm.get(error.fieldName);
      if (control) {
        if (!firstErrorFieldName) {
          firstErrorFieldName = error.fieldName;
        }
        this.formErrors[error.fieldName] += error.message + " ";
      } else {
        unknownErrors.push(error.message);
      }
    }

    if (!errors || errors.message) {
      this.serverMessage = errors ? errors.message : null;
    } else if (unknownErrors.length > 0) {
      this.serverMessage = unknownErrors.join(" ");
    }

    if (!this.serverMessage) {
      this.serverMessage =
        "Your transaction could not be processed. Please try again or contact NBRC.";
    }

    this.viewState.stopLoading();

    this.notificationsService.error(
      "Payment Failed",
      "Your transaction was not processed. Please check your payment information and try again or contact NBRC."
    );

    setTimeout(function () {
      const target = <HTMLElement>(
        (document.getElementById(firstErrorFieldName)
          ? document.getElementById(firstErrorFieldName)
          : document.getElementById("serverMessage"))
      );
      console.log(target);
      target.scrollIntoView();
    }, 750);
  }

  private mapPurchaseToPayment(product: IRenewal[], opaqueData) {
    let payment = new RenewalPayment();
    let paymentDetail = new RenewalDetail();

    product.forEach((p) => {
      this.source = p.source;
      paymentDetail = new RenewalDetail();
      paymentDetail.Cost = p.price;
      paymentDetail.RenewalYear = parseInt(p.renewalYear);
      paymentDetail.ServiceCodeID = p.serviceCodeId;
      payment.statusCode = p.status;
      payment.Details.push(paymentDetail);
    });
    payment.address = this.formValues.address;
    payment.cardCode = ""; //this.formValues.ccv;
    payment.cardNumber = ""; //this.formValues.cardNumber;
    payment.city = this.formValues.city;
    payment.expirationDate = ""; //this.formValues.expirationDate.replace("/", "");
    payment.payerName = this.formValues.nameOnCard;
    payment.paymentType = "CC";
    payment.personID = this.user.personId;
    payment.state = this.formValues.state;
    payment.totalCost = this.purchaseState.currentState.total;
    payment.zip = this.formValues.zip;
    payment.countryId = this.formValues.countryId;
    payment.dataDescriptor = opaqueData.dataDescriptor;
    payment.dataValue = opaqueData.dataValue;
    return payment;
  }

  private buildForm() {
    this.checkoutForm = this.formBuilder.group({
      nameOnCard: [this.formValues.nameOnCard, [Validators.required]],
      cardNumber: [
        this.formValues.cardNumber,
        [
          Validators.required,
          Validators.minLength(15),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      expirationDate: [this.formValues.expirationDate, [Validators.required]],
      ccv: [
        this.formValues.ccv,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      address: [this.formValues.address, [Validators.required]],
      city: [this.formValues.city, [Validators.required]],
      state: [this.formValues.state, [Validators.required]],
      zip: [this.formValues.zip, [Validators.required]],
      countryId: [this.formValues.countryId, Validators.required],
    });
  }

  private validateForm() {
    this.serverMessage = null;

    for (const control in this.checkoutForm.controls) {
      this.checkoutForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.checkoutForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) this.viewState.stopLoading();
  }

  formErrors = {
    nameOnCard: "",
    cardNumber: "",
    expirationDate: "",
    ccv: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    countryId: "",
  };

  private validationMessages = {
    nameOnCard: {
      required: "Name on Card is required.",
      requiredIfNot: "Name on Card is required.",
    },
    cardNumber: {
      required: "Card Number is required.",
      requiredIfNot: "Card Number is required.",
      minlength: "Card Number is invalid.",
      pattern: "Card Number is invalid.",
    },
    expirationDate: {
      required: "Expiration is required.",
      requiredIfNot: "Expiration is required.",
    },
    ccv: {
      required: "CCV is required.",
      requiredIfNot: "CCV is required.",
      minlength: "CCV is invalid.",
      pattern: "CCV is invalid.",
    },
    address: {
      required: "Address is required.",
    },
    city: {
      required: "City is required.",
    },
    state: {
      required: "State is required.",
    },
    zip: {
      required: "Postal Code is required.",
    },
    countryId: {
      required: "Country is required.",
    },
  };

  private formValues = {
    nameOnCard: "",
    cardNumber: "",
    expirationDate: "",
    ccv: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    countryId: "",
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
